import { useEffect, useId, useRef, useState } from 'react';

import {
  Popover,
  PopoverAnchor,
  PopoverContent
} from '@/components/ui/popover';
import type { UtcDetailModel } from './model/utcDetailModel';

const WHAT_THIS_MEANS_COPY =
  'Once the on-site issue is resolved for this property, please place a new order.';

const HOVER_CLOSE_DELAY_MS = 120;

function SectionLabel({ children }: { children: string }) {
  return (
    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </span>
  );
}

/** Red "UTC" chip with a hover/focus detail card. Click pins the card open;
    Esc or an outside click closes it. Rendered only for UTC orders. */
export function UtcBadge({ detail }: { detail: UtcDetailModel }) {
  const [open, setOpen] = useState(false);
  const pinnedRef = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const cardId = useId();

  // Clear any pending hide timer on unmount so it can't fire against a
  // detached component and trigger a setState-after-unmount warning.
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const scheduleHide = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      if (!pinnedRef.current) setOpen(false);
    }, HOVER_CLOSE_DELAY_MS);
  };
  const close = () => {
    clearTimeout(closeTimer.current);
    pinnedRef.current = false;
    setOpen(false);
  };

  const label = `Unable to Complete — see details${
    detail.reason ? `: ${detail.reason}` : ''
  }`;

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>
        <button
          type="button"
          aria-label={label}
          aria-expanded={open}
          aria-describedby={open ? cardId : undefined}
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
          onFocus={show}
          onBlur={scheduleHide}
          onClick={() => {
            pinnedRef.current = true;
            show();
          }}
          className="inline-flex h-[18px] shrink-0 cursor-pointer items-center rounded-sm bg-destructive-soft px-1.5 text-[10px] font-bold tracking-wider text-destructive-soft-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-destructive"
        >
          UTC
        </button>
      </PopoverAnchor>
      <PopoverContent
        id={cardId}
        role="tooltip"
        align="start"
        side="bottom"
        className="w-80 overflow-hidden border-l-4 border-l-destructive p-0"
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onEscapeKeyDown={close}
        onPointerDownOutside={close}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-center gap-2 border-b border-destructive-soft bg-destructive-soft/60 px-4 py-3">
          <span
            aria-hidden="true"
            className="h-2 w-2 shrink-0 rounded-full bg-destructive"
          />
          <span className="text-sm font-bold text-destructive-soft-foreground">
            Marked Unable to Complete
          </span>
        </div>
        <div className="flex flex-col gap-3 px-4 py-3.5">
          {detail.reason && (
            <div className="flex flex-col gap-1">
              <SectionLabel>Reason</SectionLabel>
              <span className="self-start rounded-full bg-destructive-soft px-2.5 py-0.5 text-xs font-semibold text-destructive-soft-foreground">
                {detail.reason}
              </span>
            </div>
          )}
          {detail.notes && (
            <div className="flex flex-col gap-1">
              <SectionLabel>Note from the PlanOtech</SectionLabel>
              <span className="text-[13px] leading-relaxed text-foreground">
                {detail.notes}
              </span>
            </div>
          )}
          {(detail.reason || detail.notes) && (
            <div className="h-px bg-border" />
          )}
          {detail.originalServicesText && (
            <div className="flex flex-col gap-0.5">
              <SectionLabel>Original Order</SectionLabel>
              <span className="text-[13px] font-medium text-foreground">
                {detail.originalServicesText}
              </span>
              {detail.submittedDateText && (
                <span className="text-xs text-muted-foreground">
                  Submitted {detail.submittedDateText}
                </span>
              )}
            </div>
          )}
          <div className="rounded-md border border-border bg-info-soft px-3 py-2.5">
            <span className="block text-xs font-semibold text-info-soft-foreground">
              What This Means
            </span>
            <span className="block text-xs leading-relaxed text-foreground">
              {WHAT_THIS_MEANS_COPY}
            </span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
